import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API } from '../App';
import { clearWelcomeAccess } from '../utils/welcomeAccess';
import {
  markFirstOnboardingSession,
  clearFirstOnboardingSession,
} from '../utils/guestOnboarding';
import { setUserId, setUserProperties, trackEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import { peekGuestId } from '../utils/guestId';

import { hasPremiumAccess } from '../config/subscriptions';
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      console.log('[AuthContext] Checking auth...');
      
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        timeout: 10000
      });
      
      setUser(response.data);
      console.log('[AuthContext] User loaded:', response.data.email);

      // Mark this as the "first onboarding session" for guest-role users who
      // haven't finished onboarding yet so the Guide button stays visible
      // throughout this whole session — only the NEXT login (after logout)
      // will hide it.
      if (
        (response.data.role === 'guest' || response.data.role === 'user') &&
        response.data.onboarding_completed === false
      ) {
        markFirstOnboardingSession();
      }

      // Restore GA4 user-properties for the session — gtag user_properties
      // don't persist across reloads, so we re-set them on every checkAuth.
      // This is critical for Path / Funnel Explorations that filter on
      // user_role / subscription_type / machine_name.
      setUserId(response.data.id);
      setUserProperties({
        user_role: response.data.role || 'user',
        subscription_type: response.data.subscription_type || 'free',
        machine_name: response.data.machine_name || null,
      });

      // Store session token in localStorage for reference
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
    } catch (error) {
      console.log('[AuthContext] Auth check failed:', error.response?.status || error.message);
      
      // Clear session on 401/403
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('session_token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    if (!userData) {
      console.error('[AuthContext] Login called with undefined userData');
      return;
    }

    setUser(userData);
    console.log('[AuthContext] User logged in:', userData.email || 'Unknown');

    // Same "first session" semantics as checkAuth(): if this is a fresh
    // guest-role login that still has onboarding pending, keep the Guide
    // button visible until the user logs out and back in.
    if (
      (userData.role === 'guest' || userData.role === 'user') &&
      userData.onboarding_completed === false
    ) {
      markFirstOnboardingSession();
    }

    // GA4 + DB tracking
    setUserId(userData.id);
    setUserProperties({
      user_role: userData.role || 'user',
      subscription_type: userData.subscription_type || 'free',
      machine_name: userData.machine_name || null,
    });
    // `userData.is_new_signup` is set by SignupPage when it calls login(),
    // so we can split sign_up (one-time) from login (recurring) cleanly
    // without double-counting on the AuthContext side.
    if (userData.is_new_signup) {
      trackEvent(ANALYTICS_EVENTS.SIGN_UP, {
        method: userData.signup_method || 'email',
      });
    } else {
      trackEvent(ANALYTICS_EVENTS.LOGIN, {
        method: 'email',
      });
    }

    // Store session token
    if (userData.session_token) {
      localStorage.setItem('session_token', userData.session_token);
    }

    // Flyt eventuelle gæste-reaktioner over på brugerens konto.
    // Fire-and-forget — må aldrig blokere login-flowet.
    try {
      const gid = peekGuestId();
      if (gid) {
        axios
          .post(`${API}/reactions/migrate`, { guest_id: gid })
          .then((r) => {
            const migrated = r?.data?.migrated || 0;
            const deduped = r?.data?.deduped || 0;
            if (migrated || deduped) {
              console.info('[AuthContext] reactions migration:', r.data);
            }
          })
          .catch((err) => {
            console.debug('[AuthContext] reactions migration failed', err?.response?.status);
          });
      }
    } catch (_e) {
      /* never block login */
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear user state and localStorage
      setUser(null);
      localStorage.removeItem('session_token');
      // Drop any temporary onboarding welcome unlock so Pro recipes lock again
      clearWelcomeAccess();
      // Drop the "first onboarding session" marker so the next login starts
      // in post-onboarding mode (Guide button hidden).
      clearFirstOnboardingSession();

      trackEvent(ANALYTICS_EVENTS.LOGOUT);
      setUserId(null);
      setUserProperties({
        user_role: 'guest',
        subscription_type: 'free',
        machine_name: null,
      });

      // Reset language to Danish (default) on logout
      localStorage.removeItem('user_language');
      localStorage.removeItem('user_country');
      localStorage.removeItem('user_country_manual');
      localStorage.removeItem('user_country_timestamp');
      
      // Change i18n back to Danish
      if (window.i18n) {
        window.i18n.changeLanguage('da');
      }
      
      console.log('[AuthContext] Logged out');
    }
  };

  const isGuest = () => !user;  // Kun anonyme (ikke logget ind). Free-tier = registreret bruger, ikke gæst.
  // isPro() betyder nu "har premium-adgang" (Light OR Pro OR editor/admin).
  // Beslutning per user 08-Jul-2026: Light og Pro har SAMME funktionelle
  // adgang. Ubegrænsede egne opskrifter, alle features, ingen låse.
  // Bruger den centrale `hasPremiumAccess`-helper så både frontend-flags
  // og backend-endpoints er enige.
  const isPro = () => hasPremiumAccess(user);
  const isEditor = () => user?.role === 'editor' || user?.role === 'admin';
  const isAdmin = () => user?.role === 'admin';

  // Update user's completed tours (called after tour completion)
  const updateCompletedTours = (tourId) => {
    if (user) {
      setUser({
        ...user,
        completed_tours: [...(user.completed_tours || []), tourId]
      });
      console.log('[AuthContext] Updated completed_tours with:', tourId);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      checkAuth,
      updateCompletedTours,
      isGuest,
      isPro,
      isEditor,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
